import { useState } from "react";
import styled from "@emotion/styled";
import { Route, Router, Switch } from "wouter";

import BidSelfServe from "components/account_management/qualification-criteria/BidSelfServe";
import { BidModuleCustomHeader } from "components/bid_module/BidModuleExternalHeader";
import { BidNoBidPage } from "components/bid_module/BidNoBidPage";
import { BidWritingQuestionsPage } from "components/bid_module/BidWritingQuestionsPage";
import OpportunityPipelineEmptyState from "components/opportunities/empty_states/OpportunityPipelineEmptyState";
import {
  DEFAULT_PAGINATION,
  EMPTY_OPPORTUNITY_FILTERS,
  getDefaultOpportunityStageIds,
  OpportunityFilters,
} from "components/opportunities/utils";
import { useOpportunities } from "lib/hooks/api/opportunities/useOpportunities";
import { useOpportunityWorkflow } from "lib/hooks/api/opportunities/useOpportunityWorkflow";
import { EventNames, useTracking } from "lib/tracking";
import { withAppLayout } from "../../components/app_layout/AppLayout";
import { DetailsContent } from "../../components/app_layout/DetailsLayout";
import { CreateOpportunityDrawer } from "../../components/opportunities/create_opportunity/CreateOpportunityDrawer";
import { FilterableOpportunities } from "../../components/opportunities/FilterableOpportunities";
import Button from "../../components/ui/button/Button";
import CentredSpinner from "../../lib/core_components/CentredSpinner";
import { Flex, Text } from "../../styles/utility-components";
import OpportunityPage from "./OpportunityPage";

import css from "./OpportunitiesPage.module.scss";

function OpportunitiesPageWrapper() {
  return (
    <Router base={`/opportunities`}>
      <Switch>
        <Route path="/:id/bid_qualification/:bidId/bid-self-serve" component={BidSelfServe} />
        <Route path="/:id/bid_qualification/:bidId?">
          {(params) => <BidNoBidPage opportunityId={params.id} from="opportunityPage" />}
        </Route>
        <Route path="/:id/bid_writing_questions/:bidId?">
          {(params) => <BidWritingQuestionsPage opportunityId={params.id} bidId={params.bidId} />}
        </Route>
        <Route path="/:id">{(params) => <OpportunityPage id={params.id} />}</Route>
        <Route path="/" component={OpportunitiesPage} />
      </Switch>
    </Router>
  );
}

export function OpportunitiesPage() {
  const { logEvent } = useTracking();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data: opportunityWorkflowData, isLoading: isLoadingWorkflowData } =
    useOpportunityWorkflow();

  const defaultFilters: OpportunityFilters = opportunityWorkflowData
    ? {
        ...EMPTY_OPPORTUNITY_FILTERS,
        stageIds: getDefaultOpportunityStageIds(opportunityWorkflowData.transitions),
        // A non user shouldn't get here, but can't use `assertCurrentUser`
        // here because it makes external bids sharing fail
        assignee: window.currentUser?.guid
          ? { ids: [window.currentUser?.guid], includeNulls: false }
          : undefined,
      }
    : {};

  const { data: initialOpportunities, isLoading: isLoadingInitialOpportunities } = useOpportunities(
    {},
    DEFAULT_PAGINATION,
  );

  if (isLoadingWorkflowData || isLoadingInitialOpportunities) {
    return <CentredSpinner />;
  }

  const isPipelineEmpty = initialOpportunities?.total === 0;

  return (
    <DetailsContent className={css.container}>
      <Flex column gap={16} height="100%">
        <Flex justifyContent="space-between">
          <Text h1>Opportunities</Text>
          <Button
            onClick={() => {
              setIsCreateModalOpen(true);
              logEvent(EventNames.createOpportunityStarted, {
                "Context source": "Create opportunity modal",
              });
            }}
          >
            Create Opportunity
          </Button>
        </Flex>
        {isPipelineEmpty ? (
          <Wrapper>
            <OpportunityPipelineEmptyState
              eventData={{
                "Context source": "Opportunities table",
              }}
            />
          </Wrapper>
        ) : (
          <FilterableOpportunities defaultFilters={defaultFilters} />
        )}
        <CreateOpportunityDrawer
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      </Flex>
    </DetailsContent>
  );
}

const Wrapper = styled(Flex)(({ theme }) => ({
  border: `1px solid ${theme.colors.sysBorderPrimary}`,
  borderRadius: "4px",
  height: "100%",
  justifyContent: "center",
  alignItems: "center",
}));

export default withAppLayout(OpportunitiesPageWrapper, {
  displayCustomAction: !window.currentUser &&
    window.location.href.includes("bid_qualification") && <BidModuleCustomHeader />,
});
